import { Link, useNavigate } from 'react-router';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Eyebrow, Pill, Skeleton } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { dueSentence, todayKey } from '../tasks/dates';
import { useQueueSummary, useStartQueue, type SessionSummary } from './api';
import styles from './QueueSummary.module.css';

function reworkLabel(count: number): string {
  if (count === 1) return 'Jetzt bearbeiten';
  if (count === 2) return 'Beide jetzt bearbeiten';
  return 'Alle jetzt bearbeiten';
}

/** Queue 10 (open items) and Queue 11 (everything done) after „Beenden" — also after a reload via `?summary=`. */
export function QueueSummary({ sessionId }: { sessionId: string }) {
  const navigate = useNavigate();
  const summary = useQueueSummary(sessionId);
  const start = useStartQueue();
  const today = todayKey();

  if (summary.isError) {
    return (
      <div className={styles.page}>
        <div className={styles.narrow}>
          <Banner
            tone="danger"
            actions={
              <Button variant="secondary" size="small" onClick={() => void navigate('/tasks')}>
                Zu Meine Tasks
              </Button>
            }
          >
            {errorMessage(summary.error)}
          </Banner>
        </div>
      </div>
    );
  }
  if (summary.isPending) {
    return (
      <div className={styles.page} aria-busy="true" aria-label="Wird geladen">
        <div className={styles.wide}>
          <div className={styles.statsCard}>
            <Skeleton width={160} height={8} />
            <Skeleton width="50%" height={26} />
            <Skeleton width="80%" />
          </div>
        </div>
      </div>
    );
  }

  const data: SessionSummary = summary.data;
  const nextDueText = data.nextDue
    ? `Die nächste fällige Task kommt ${dueSentence(data.nextDue.on, data.nextDue.time, today)}.`
    : 'Es ist keine weitere Task geplant.';

  if (data.allDone) {
    return (
      <div className={styles.page}>
        <div className={styles.narrow}>
          <div className={styles.doneCard}>
            <Eyebrow>Session beendet · {data.durationLabel}</Eyebrow>
            <div className={styles.doneTitle}>Alles erledigt.</div>
            <div className={styles.doneText}>
              {data.total === 1 ? '1 Task abgeschlossen' : `${data.total} Tasks abgeschlossen`}, jede mit
              Folgeschritt. {nextDueText}
            </div>
            <div className={styles.doneActions}>
              <Button onClick={() => void navigate('/tasks')}>Zu Meine Tasks</Button>
              <Button variant="secondary" onClick={() => void navigate('/calendar')}>
                Kalender öffnen
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const skipped = data.openItems.filter((item) => item.status === 'skipped');
  const reworkIds = skipped.flatMap((item) => (item.taskId === null ? [] : [item.taskId]));

  return (
    <div className={styles.page}>
      <div className={styles.wide}>
        <div className={styles.statsCard}>
          <Eyebrow>Session beendet · {data.durationLabel}</Eyebrow>
          <div className={styles.statsTitle}>
            {data.done} von {data.total} Tasks erledigt.
          </div>
          <div className={styles.stats}>
            <div>
              <div className={styles.statValue}>{data.done}</div>
              <div className={styles.statLabel}>erledigt</div>
            </div>
            <div>
              <div className={styles.statValue}>{data.skipped}</div>
              <div className={styles.statLabel}>übersprungen</div>
            </div>
            <div>
              <div className={styles.statValue}>{data.later}</div>
              <div className={styles.statLabel}>auf später</div>
            </div>
            <div>
              <div className={styles.statValue}>{data.appointmentsBooked}</div>
              <div className={styles.statLabel}>Termine gebucht</div>
            </div>
          </div>
        </div>

        {data.openItems.length > 0 ? (
          <div className={styles.itemsCard}>
            <div className={styles.itemsHead}>
              <Eyebrow>{skipped.length > 0 ? 'Übersprungen' : 'Auf später'}</Eyebrow>
              {reworkIds.length > 0 ? (
                <span className={styles.itemsAction}>
                  <Button
                    variant="secondary"
                    size="small"
                    loading={start.isPending}
                    onClick={() =>
                      start.mutate(
                        { filter: { taskIds: reworkIds } },
                        { onSuccess: () => void navigate('/tasks/queue/session') },
                      )
                    }
                  >
                    {reworkLabel(reworkIds.length)}
                  </Button>
                </span>
              ) : null}
            </div>
            {start.isError ? (
              <div className={styles.itemsError}>
                <Banner tone="danger">{errorMessage(start.error)}</Banner>
              </div>
            ) : null}
            <div className={styles.items}>
              {data.openItems.map((item) => (
                <div key={item.entryId} className={styles.item}>
                  <div className={styles.itemText}>
                    <Link to={item.href} className={styles.itemTitle}>
                      {item.title}
                    </Link>
                    <div className={styles.itemStatus}>{item.statusText}</div>
                  </div>
                  <Pill variant={item.status === 'skipped' ? 'warning' : 'neutral'}>{item.statusLabel}</Pill>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className={clsx(styles.actions)}>
          <Button variant="secondary" onClick={() => void navigate('/tasks')}>
            Zu Meine Tasks
          </Button>
          <Button onClick={() => void navigate('/tasks/queue')}>Neue Queue starten</Button>
        </div>
      </div>
    </div>
  );
}
